/**
 * useRunProgress (ENG-367)
 *
 * The single hook UI surfaces use to read live progress for a planning run
 * (keyed by epic request id) or a build run (keyed by epic id).
 *
 * Data flow:
 * - Initial load: REST
 *     planning -> GET /epic-requests/:id/run-progress
 *     build    -> GET /sessions/:epicId/run-progress
 * - Live: the API broadcasts a `progress.updated` SSE event carrying a full
 *   RunProgress snapshot. `useLiveUpdates` writes it straight into this hook's
 *   React Query cache entry via `applyRunProgressEvent` (./runProgressCache), so there is no
 *   refetch round trip.
 * - Fallback: while SSE is not connected, poll at one interval
 *   (RUN_PROGRESS_FALLBACK_POLL_MS). Polling stops entirely once the run is in
 *   a terminal state (completed / failed / cancelled).
 */

import { useQuery } from "@tanstack/react-query";
import type {
  RunKind,
  RunProgress,
  RunState,
} from "@mycompbox/shared";
import { api } from "@/lib/api/client";
import { RUN_PROGRESS_FALLBACK_POLL_MS } from "@/lib/polling";
import { useLiveUpdatesStatus } from "@/providers/LiveUpdatesProvider";
import type { ConnectionStatus } from "./useEventSource";
import { runProgressKeys } from "./runProgressCache";

export { runProgressKeys, applyRunProgressEvent, RUN_PROGRESS_SSE_EVENT } from "./runProgressCache";

const TERMINAL_STATES: ReadonlySet<RunState> = new Set<RunState>([
  "completed",
  "failed",
  "cancelled",
]);

/** True once a run will make no more progress. */
export function isTerminalRunState(state: RunState | null | undefined): boolean {
  return state != null && TERMINAL_STATES.has(state);
}

/**
 * Decide the React Query refetch interval: poll only while SSE is not
 * connected, and never once the run is terminal.
 */
export function getRunProgressPollInterval(
  connection: ConnectionStatus,
  progress: RunProgress | null | undefined,
): number | false {
  if (isTerminalRunState(progress?.state)) return false;
  if (connection === "connected") return false;
  return RUN_PROGRESS_FALLBACK_POLL_MS;
}

export async function fetchRunProgress(kind: RunKind, id: string): Promise<RunProgress> {
  const path =
    kind === "planning"
      ? `/epic-requests/${encodeURIComponent(id)}/run-progress`
      : `/sessions/${encodeURIComponent(id)}/run-progress`;
  const res = await api.get<{ data: RunProgress }>(path);
  return res.data;
}

export interface UseRunProgressResult {
  progress: RunProgress | null;
  isLoading: boolean;
  error: Error | null;
  connection: ConnectionStatus;
}

/**
 * Live RunProgress for a planning run (`kind: "planning"`, id = epic request
 * id) or a build run (`kind: "build"`, id = epic id). Pass a null/undefined
 * id to disable.
 */
export function useRunProgress(
  kind: RunKind,
  id: string | null | undefined,
): UseRunProgressResult {
  const connection = useLiveUpdatesStatus();
  const enabled = typeof id === "string" && id.length > 0;

  const query = useQuery({
    queryKey: runProgressKeys.detail(kind, id ?? ""),
    queryFn: () => fetchRunProgress(kind, id ?? ""),
    enabled,
    refetchInterval: (q) => getRunProgressPollInterval(connection, q.state.data),
  });

  return {
    progress: query.data ?? null,
    isLoading: enabled && query.isLoading,
    error: query.error ?? null,
    connection,
  };
}
